import { Input, InputRef } from "antd";
import { useEffect, useRef, useState } from "react";

const GROUPS = [2, 3, 2, 2];
const MAX_LOCAL_DIGITS = 9;

const formatLocalDigits = (local: string) => {
  let result = "";
  let idx = 0;
  for (const size of GROUPS) {
    if (idx >= local.length) break;
    result += (result ? " " : "") + local.slice(idx, idx + size);
    idx += size;
  }
  return result;
};

const toDisplayValue = (localDigits: string) => (localDigits ? `+998 ${formatLocalDigits(localDigits)}` : "");

const extractLocalDigits = (raw: string) => {
  let digits = raw.replace(/\D/g, "");
  if (digits.startsWith("998")) digits = digits.slice(3);
  return digits.slice(0, MAX_LOCAL_DIGITS);
};

const countDigitsBefore = (text: string, pos: number) => text.slice(0, pos).replace(/\D/g, "").length;

const cursorForDigitCount = (display: string, digitCount: number) => {
  let seen = 0;
  for (let i = 0; i < display.length; i++) {
    if (/\d/.test(display[i])) {
      seen++;
      if (seen === digitCount) return i + 1;
    }
  }
  return display.length;
};

interface Props {
  value?: string;
  onChange?: (value: string) => void;
}

const PhoneInput = ({ value, onChange }: Props) => {
  const inputRef = useRef<InputRef>(null);
  const [displayValue, setDisplayValue] = useState(() => toDisplayValue(extractLocalDigits(value || "")));

  useEffect(() => {
    const nextDigits = extractLocalDigits(value || "");
    const canonical = toDisplayValue(nextDigits);
    setDisplayValue((current) => (extractLocalDigits(current) === nextDigits ? current : canonical));
    if (value && value !== canonical) onChange?.(canonical);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const cursor = e.target.selectionStart ?? raw.length;
    const rawDigitsBeforeCursor = countDigitsBefore(raw, cursor);
    const hasPrefix = raw.replace(/\D/g, "").startsWith("998");
    const localDigitsBeforeCursor = Math.max(0, hasPrefix ? rawDigitsBeforeCursor - 3 : rawDigitsBeforeCursor);

    const localDigits = extractLocalDigits(raw);
    const nextDisplay = toDisplayValue(localDigits);
    const targetDigitCount = 3 + Math.min(localDigitsBeforeCursor, localDigits.length);

    setDisplayValue(nextDisplay);
    onChange?.(nextDisplay);

    requestAnimationFrame(() => {
      const pos = localDigits.length ? cursorForDigitCount(nextDisplay, targetDigitCount) : 0;
      inputRef.current?.input?.setSelectionRange(pos, pos);
    });
  };

  return (
    <Input
      ref={inputRef}
      value={displayValue}
      onChange={handleChange}
      placeholder="+998 90 123 45 67"
      inputMode="tel"
      maxLength={17}
    />
  );
};

export default PhoneInput;
